import { Injectable, signal } from '@angular/core';

export interface Alert {
  id: number;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  title?: string;
  closing: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private readonly alertsSignal = signal<Alert[]>([]);
  readonly alerts = this.alertsSignal.asReadonly();

  private nextId = 1;
  private maxAlerts = 4;
  private readonly autoCloseDuration = 2500;
  private readonly closingDuration = 350;
  private readonly autoCloseTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly removeTimers = new Map<number, ReturnType<typeof setTimeout>>();

  private addAlert(message: string, type: Alert['type'], title?: string): void {
    const newAlert: Alert = {
      id: this.nextId++,
      type,
      message,
      title,
      closing: false
    };

    this.alertsSignal.update(alerts => [newAlert, ...alerts]);
    this.scheduleAutoClose(newAlert.id);
    this.trimOverflow();
  }

  success(message: string, title?: string): void {
    this.addAlert(message, 'success', title);
  }

  error(message: string, title?: string): void {
    this.addAlert(message, 'error', title);
  }

  info(message: string, title?: string): void {
    this.addAlert(message, 'info', title);
  }

  warn(message: string, title?: string): void {
    this.addAlert(message, 'warning', title);
  }

  removeAlert(id: number): void {
    const target = this.alertsSignal().find(alert => alert.id === id);

    if (!target) {
      return;
    }

    this.clearAutoCloseTimer(id);

    if (target.closing) {
      this.destroyAlert(id);
      return;
    }

    this.alertsSignal.update(alerts =>
      alerts.map(alert =>
        alert.id === id
          ? {...alert, closing: true}
          : alert
      )
    );

    const removeTimer = setTimeout(() => {
      this.destroyAlert(id);
    }, this.closingDuration);

    this.removeTimers.set(id, removeTimer);
  }

  private scheduleAutoClose(id: number): void {
    this.clearAutoCloseTimer(id);

    const timer = setTimeout(() => {
      this.removeAlert(id);
    }, this.autoCloseDuration);

    this.autoCloseTimers.set(id, timer);
  }

  private trimOverflow(): void {
    const activeAlerts = this.alertsSignal().filter(alert => !alert.closing);

    if (activeAlerts.length <= this.maxAlerts) {
      return;
    }

    const oldestActive = activeAlerts[activeAlerts.length - 1];
    this.removeAlert(oldestActive.id);
  }

  private destroyAlert(id: number): void {
    this.clearAutoCloseTimer(id);
    this.clearRemoveTimer(id);

    this.alertsSignal.update(alerts =>
      alerts.filter(alert => alert.id !== id)
    );
  }

  private clearAutoCloseTimer(id: number): void {
    const timer = this.autoCloseTimers.get(id);

    if (!timer) {
      return;
    }

    clearTimeout(timer);
    this.autoCloseTimers.delete(id);
  }

  private clearRemoveTimer(id: number): void {
    const timer = this.removeTimers.get(id);

    if (!timer) {
      return;
    }

    clearTimeout(timer);
    this.removeTimers.delete(id);
  }
}
